/**
 * Vision Platform presentation adapter for Operational Readiness.
 *
 * Dependency semantics (Phase 18):
 * - Camera existence alone does not make Camera Analytics / Correlation / Vehicle Detection Ready.
 * - Vehicle Detection readiness requires an eligible camera with vehicle-detection analytics enabled.
 * - Correlation readiness requires the same vehicle-detection prerequisite (events to correlate).
 * - Organization Ready uses any eligible loaded camera (not first/newest sampling).
 * - Organization Ready does not require every camera to be Ready.
 */

import {VisionContext,} from '../../../models/vte';
import {
    customerAnalyticsName,
    customerVisionModeLabel,
    hasVisionAnalytics,
    isVehicleDetectionAnalyticsKey,
} from '../../../services/vte/vision-customer-labels';
import {OperationalStatusCardView, OperationalWarningView,} from '../operational-readiness.models';
import {
    anyLoadedCameraHasVehicleDetection,
    CameraReadinessRow,
    eligibleCameraCorrelationWindowSeconds,
} from './shared';

const STICKER_KEY = 'sticker_verification';
const TOW_KEY = 'tow_vehicle_detection';

export type VisionPlatformAdapterInput = {
    visionContext: VisionContext | null;
    cameras: CameraReadinessRow[] | null;
};

function readyCard(
    key: string,
    title: string,
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'ready',
        statusLabel: 'Ready',
        detail,
    };
}

function informationalCard(
    key: string,
    title: string,
    detail: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'informational',
        statusLabel: 'Not enabled',
        detail,
    };
}

function pendingCard(
    key: string,
    title: string,
    warning: OperationalWarningView,
    status: 'missing' | 'warning' | 'blocked' = 'missing',
    statusLabel = 'Pending',
    detail?: string,
): OperationalStatusCardView {
    return {
        key,
        title,
        status,
        statusLabel,
        detail,
        warnings: [warning],
    };
}

function unknownCard(key: string, title: string, detail: string): OperationalStatusCardView {
    return {
        key,
        title,
        status: 'unknown',
        statusLabel: 'Unknown',
        detail,
    };
}

function capabilityEnabled(capabilities: string[], key: string): boolean {
    return (capabilities || []).includes(key);
}

function activeVehicleDetectionKey(capabilities: string[]): string | null {
    return (
        (capabilities || []).find((key) => isVehicleDetectionAnalyticsKey(key)) ||
        null
    );
}

function vehicleDetectionLabel(capabilities: string[]): string {
    const key = activeVehicleDetectionKey(capabilities);
    if (!key) {
        return 'Vehicle Detection';
    }
    return customerAnalyticsName({analytics_key: key, name: 'Vehicle Detection'});
}

function enabledAnalyticsCountForCamera(camera: CameraReadinessRow): number {
    return camera.status?.enabled_analytics_count || 0;
}

/**
 * Optional capability pattern:
 * - not enabled → informational, no warning
 * - enabled → ready, or warning when known misconfiguration exists
 */
function mapOptionalCapabilityCard(
    key: string,
    title: string,
    enabled: boolean,
    misconfiguration: OperationalWarningView | null,
): OperationalStatusCardView {
    if (!enabled) {
        return informationalCard(
            key,
            title,
            'Not enabled for this organization.',
        );
    }
    if (misconfiguration) {
        return pendingCard(key, title, misconfiguration, 'warning', 'Warning');
    }
    return readyCard(key, title, 'Enabled in organization vision settings.');
}

function mapAnalyticsCard(
    mode: string,
    modeLabel: string,
    verificationEnabled: boolean,
): OperationalStatusCardView {
    if (mode === 'disabled' && verificationEnabled) {
        return pendingCard(
            'vision_configuration',
            'Analytics',
            {
                problem: 'Vision mode is Disabled while vision analytics remain enabled.',
                impact: 'Verification analytics may not run as operators expect.',
                recommendedAction:
                    'Open Vision Configuration and set Monitor Only or Enforced, or disable vision analytics.',
                scope: 'organization',
            },
            'warning',
            'Warning',
            `Mode: ${modeLabel}`,
        );
    }
    return readyCard('vision_configuration', 'Analytics', `Mode: ${modeLabel}`);
}

function mapCameraAnalyticsCard(
    cameras: CameraReadinessRow[] | null,
): OperationalStatusCardView {
    if (cameras === null) {
        return unknownCard(
            'camera_analytics',
            'Camera Analytics',
            'Camera list could not be loaded to evaluate camera analytics configuration.',
        );
    }
    if (cameras.length === 0) {
        return pendingCard(
            'camera_analytics',
            'Camera Analytics',
            {
                problem: 'No camera is registered for this organization.',
                impact: 'Camera analytics cannot be configured until a camera exists.',
                recommendedAction: 'Add a camera for this organization.',
                scope: 'organization',
                condition: 'Requires at least one camera',
            },
        );
    }
    const eligible = cameras.filter((camera) => enabledAnalyticsCountForCamera(camera) > 0);
    if (eligible.length === 0) {
        return pendingCard(
            'camera_analytics',
            'Camera Analytics',
            {
                problem: 'Cameras are registered, but no analytics are enabled on any loaded camera.',
                impact:
                    'Vehicle detection and enrichment will not run for cameras without enabled analytics.',
                recommendedAction:
                    'Open Camera Analytics, enable the required analytics for at least one camera, and save.',
                scope: 'organization',
                condition: 'Requires enabled analytics on at least one camera',
            },
            'missing',
            'Pending',
            '0 loaded camera(s) with enabled analytics.',
        );
    }

    const enabledCount = eligible.reduce(
        (max, camera) => Math.max(max, enabledAnalyticsCountForCamera(camera)),
        0,
    );
    return readyCard(
        'camera_analytics',
        'Camera Analytics',
        `${eligible.length} loaded camera(s) with enabled analytics` +
        ` (up to ${enabledCount} enabled analytic(s) on a camera).`,
    );
}

function mapCorrelationCard(
    cameras: CameraReadinessRow[] | null,
    vehicleDetectionEnabledOnCamera: boolean,
): OperationalStatusCardView {
    if (cameras === null) {
        return unknownCard(
            'correlation_configuration',
            'Correlation',
            'Camera list could not be loaded to evaluate correlation readiness.',
        );
    }
    if (cameras.length === 0) {
        return pendingCard(
            'correlation_configuration',
            'Correlation',
            {
                problem: 'No camera is registered for this organization.',
                impact: 'Correlation settings are not operational until cameras and analytics exist.',
                recommendedAction: 'Add a camera, then enable vehicle detection analytics.',
                scope: 'organization',
                condition: 'Requires at least one camera',
            },
        );
    }
    if (!vehicleDetectionEnabledOnCamera) {
        return pendingCard(
            'correlation_configuration',
            'Correlation',
            {
                problem:
                    'Cameras are registered, but required vehicle detection analytics are not enabled.',
                impact:
                    'Correlation only applies once cameras produce vehicle detection events to correlate.',
                recommendedAction:
                    'Enable vehicle detection analytics on at least one camera, then confirm correlation settings.',
                scope: 'organization',
                condition: 'Requires vehicle detection analytics on at least one camera',
            },
        );
    }

    const correlationSeconds = eligibleCameraCorrelationWindowSeconds(cameras);
    if (typeof correlationSeconds === 'number') {
        return readyCard(
            'correlation_configuration',
            'Correlation',
            `Correlation window: ${correlationSeconds}s (eligible camera).`,
        );
    }
    return readyCard(
        'correlation_configuration',
        'Correlation',
        'Default correlation window applies until camera correlation settings are saved.',
    );
}

function mapVehicleDetectionCard(
    cameras: CameraReadinessRow[] | null,
    capabilities: string[],
    vehicleDetectionEnabledOnCamera: boolean,
): OperationalStatusCardView {
    const vehicleKey = activeVehicleDetectionKey(capabilities);
    const label = vehicleDetectionLabel(capabilities);

    if (!vehicleKey) {
        return pendingCard(
            'vehicle_detection',
            'Vehicle Detection',
            {
                problem: 'No vehicle detection capability is selected in organization vision settings.',
                impact: 'Organization policy does not declare which vehicle detection provider is in use.',
                recommendedAction:
                    'Open Vision Configuration and enable a Vehicle Detection option.',
                scope: 'organization',
            },
        );
    }

    if (cameras === null) {
        return unknownCard(
            'vehicle_detection',
            'Vehicle Detection',
            'Camera list could not be loaded to evaluate vehicle detection readiness.',
        );
    }

    if (cameras.length === 0) {
        return pendingCard(
            'vehicle_detection',
            'Vehicle Detection',
            {
                problem: 'No camera is registered for this organization.',
                impact: 'Vehicle detection cannot run until a camera is registered and configured.',
                recommendedAction: 'Add a camera, then enable vehicle detection analytics on it.',
                scope: 'organization',
                condition: 'Requires at least one camera',
            },
            'missing',
            'Pending',
            label,
        );
    }

    if (!vehicleDetectionEnabledOnCamera) {
        return pendingCard(
            'vehicle_detection',
            'Vehicle Detection',
            {
                problem:
                    'Cameras are registered, but required vehicle detection analytics are not enabled.',
                impact: 'Incoming LPR events will not be processed as vehicle detections for this camera.',
                recommendedAction:
                    'Open Camera Analytics and enable the vehicle detection analytic for at least one camera.',
                scope: 'organization',
                condition: 'Requires enabled vehicle detection analytics on at least one camera',
            },
            'missing',
            'Pending',
            label,
        );
    }

    return readyCard('vehicle_detection', 'Vehicle Detection', label);
}

export function mapVisionPlatformCards(
    input: VisionPlatformAdapterInput,
): OperationalStatusCardView[] {
    const {visionContext, cameras} = input;

    if (visionContext === null) {
        return [
            unknownCard(
                'vision_configuration',
                'Analytics',
                'Vision configuration could not be loaded.',
            ),
        ];
    }

    const policy = visionContext.vision_policy;
    const capabilities = policy?.vision_capabilities || [];
    const mode = policy?.vision_mode || 'disabled';
    const modeLabel = customerVisionModeLabel(mode);
    const verificationEnabled = hasVisionAnalytics(capabilities);
    const vehicleDetectionEnabledOnCamera =
        cameras != null && anyLoadedCameraHasVehicleDetection(cameras);

    const cards: OperationalStatusCardView[] = [];

    // 1. Analytics (organization vision configuration)
    cards.push(mapAnalyticsCard(mode, modeLabel, verificationEnabled));

    // 2. Camera Analytics
    cards.push(mapCameraAnalyticsCard(cameras));

    // 3. Correlation
    cards.push(mapCorrelationCard(cameras, vehicleDetectionEnabledOnCamera));

    // 4. Vehicle Detection
    cards.push(mapVehicleDetectionCard(cameras, capabilities, vehicleDetectionEnabledOnCamera));

    // Optional: Sticker Verification
    const stickerMisconfig =
        capabilityEnabled(capabilities, STICKER_KEY) && mode === 'disabled'
            ? ({
                problem: 'Sticker Verification is enabled while vision mode is Disabled.',
                impact: 'Sticker verification may not run for vehicle processing.',
                recommendedAction:
                    'Set Vision Mode to Monitor Only or Enforced in Vision Configuration.',
                scope: 'organization',
                condition: 'Applies because Sticker Verification is enabled',
            } satisfies OperationalWarningView)
            : capabilityEnabled(capabilities, STICKER_KEY) &&
            verificationEnabled &&
            !String(policy.vision_provider || '').trim()
                ? ({
                    problem: 'Sticker Verification is enabled without a vision verification service selected.',
                    impact: 'Verification requests may fail until a vision service is configured.',
                    recommendedAction:
                        'Open Vision Configuration and save settings so the verification service can be applied.',
                    scope: 'organization',
                    condition: 'Applies because Sticker Verification is enabled',
                } satisfies OperationalWarningView)
                : null;
    cards.push(
        mapOptionalCapabilityCard(
            'sticker_verification',
            'Sticker Verification',
            capabilityEnabled(capabilities, STICKER_KEY),
            stickerMisconfig,
        ),
    );

    // Optional: Tow Vehicle Detection
    const towMisconfig =
        capabilityEnabled(capabilities, TOW_KEY) && mode === 'disabled'
            ? ({
                problem: 'Tow Vehicle Detection is enabled while vision mode is Disabled.',
                impact: 'Tow vehicle detection may not run for vehicle processing.',
                recommendedAction:
                    'Set Vision Mode to Monitor Only or Enforced in Vision Configuration.',
                scope: 'organization',
                condition: 'Applies because Tow Vehicle Detection is enabled',
            } satisfies OperationalWarningView)
            : capabilityEnabled(capabilities, TOW_KEY) &&
            verificationEnabled &&
            !String(policy.vision_provider || '').trim()
                ? ({
                    problem: 'Tow Vehicle Detection is enabled without a vision verification service selected.',
                    impact: 'Verification requests may fail until a vision service is configured.',
                    recommendedAction:
                        'Open Vision Configuration and save settings so the verification service can be applied.',
                    scope: 'organization',
                    condition: 'Applies because Tow Vehicle Detection is enabled',
                } satisfies OperationalWarningView)
                : null;
    cards.push(
        mapOptionalCapabilityCard(
            'tow_vehicle_detection',
            'Tow Vehicle Detection',
            capabilityEnabled(capabilities, TOW_KEY),
            towMisconfig,
        ),
    );

    return cards;
}
